import type {Mapper} from '@automapper/core';
import {constructUsing, createMap, forMember, mapFrom} from '@automapper/core';
import type {IMapping} from '@igp/shared/auto-mapper';
import type {BoUser} from '@igp/shared/contract/pam';

import {
    allRoles,
    BoUserDataGridColumnsTypeName,
    BoUserEditViewModelTypeName,
    BoUserQueryFieldsTypeName,
    BoUserSaveBasePayloadTypeName,
    BoUserTypeName,
    BoUserViewModelKeysTypeName,
    BoUserViewModelTypeName,
} from './constants';
import type {
    BoUserDataGridColumns,
    BoUserEditViewModel,
    BoUserQueryFields,
    BoUserSaveBasePayload,
    BoUserViewModel,
    BoUserViewModelKeys,
} from './types';

export class BoUserMapping implements IMapping {
    createMapping(mapper: Mapper) {
        createMap<BoUserDataGridColumns, BoUserViewModelKeys>(
            mapper,
            BoUserDataGridColumnsTypeName,
            BoUserViewModelKeysTypeName,
            constructUsing(m => {
                const boUserColumnsToViewModelKeysMap: Record<BoUserDataGridColumns, BoUserViewModelKeys> = {
                    uuid: 'id',
                    username: 'username',
                    firstName: 'firstName',
                    lastName: 'lastName',
                    email: 'email',
                    role: 'role',
                    aal: 'aalGroup',
                    agentPlayerId: 'playerId',
                    edit: 'id',
                    resetPassword: 'id',
                    accountStatus: 'isAccountLocked',
                    unlinkAgent: 'id',
                };
                return boUserColumnsToViewModelKeysMap[m];
            })
        );
        createMap<BoUser, BoUserViewModel>(
            mapper,
            BoUserTypeName,
            BoUserViewModelTypeName,
            forMember(
                vm => vm.id,
                mapFrom(m => m.id)
            ),
            forMember(
                vm => vm.username,
                mapFrom(m => m.username)
            ),
            forMember(
                vm => vm.firstName,
                mapFrom(m => m.first_name)
            ),
            forMember(
                vm => vm.lastName,
                mapFrom(m => m.last_name)
            ),
            forMember(
                vm => vm.email,
                mapFrom(m => m.email)
            ),
            forMember(
                vm => vm.role,
                mapFrom(m => m.groups?.find(group => allRoles.some(role => role === group.name)))
            ),
            forMember(
                vm => vm.aalGroup,
                mapFrom(m => {
                    const aalGroupPrefix = 'aal_';
                    const aalManagersGroupPrefix = 'aal_managers_';
                    const aalGroup = m.groups?.find(
                        group => group.name.startsWith(aalGroupPrefix) || group.name.startsWith(aalManagersGroupPrefix)
                    );
                    return aalGroup;
                })
            ),
            forMember(
                vm => vm.playerId,
                mapFrom(m => m.attributes?.player_id)
            ),
            forMember(
                vm => vm.isAccountLocked,
                mapFrom(m => !m.enabled)
            )
        );
        createMap<BoUserEditViewModel, BoUserSaveBasePayload>(
            mapper,
            BoUserEditViewModelTypeName,
            BoUserSaveBasePayloadTypeName,
            forMember(
                vm => vm.username,
                mapFrom(m => m.username)
            ),
            forMember(
                vm => vm.firstName,
                mapFrom(m => m.firstName)
            ),
            forMember(
                vm => vm.lastName,
                mapFrom(m => m.lastName)
            ),
            forMember(
                vm => vm.email,
                mapFrom(m => m.email)
            ),
            forMember(
                vm => vm.playerId,
                mapFrom(m => m.playerId)
            ),
            forMember(
                vm => vm.aalGroupId,
                mapFrom(m => m.aalGroupId)
            )
        );
        createMap<BoUserViewModelKeys, BoUserQueryFields>(
            mapper,
            BoUserViewModelKeysTypeName,
            BoUserQueryFieldsTypeName,
            constructUsing(m => {
                const mapper: Record<BoUserViewModelKeys, BoUserQueryFields> = {
                    id: 'id',
                    username: 'username',
                    firstName: 'first_name',
                    lastName: 'last_name',
                    email: 'email',
                    role: 'roles',
                    aalGroup: 'aal_group',
                    playerId: 'attributes.player_id',
                    isAccountLocked: 'enabled',
                };
                return mapper[m];
            })
        );
    }
}
